import { Controller } from '@hotwired/stimulus'
import { RecentSearchesManager } from '../utils/recent_searches'

const STATES = ['both', 'applied', 'unapplied']

// Row-highlight legend toggles: which of the four highlight colours are shown.
const HIGHLIGHT_DEFAULTS = {
  highlight_job_in_current_week: '1',
  highlight_non_regular: '1',
  highlight_assigned_to_work_list: '1',
  highlight_future_jobs: '1'
}

// Quick search on the customers index. Reloads only the results fragment via
// fetch, so searching never reloads the page. Registered on the page wrapper
// (a common ancestor of the form and the results container).
export default class extends Controller {
  static targets = ['form', 'chip', 'term', 'reset', 'results', 'title', 'clear', 'moreFiltersBadge', 'highlightToggle', 'acquisitionResults', 'recentSearchesFlyout', 'recentSearchesList']

  connect() {
    this.chipTargets.forEach((chip) => this.syncChip(chip))
    this.syncHighlightToggles()
    this.updateClearVisibility()
    this.updateMoreFiltersBadge()
    this.setupRecentSearchesClickHandler()
  }

  setupRecentSearchesClickHandler() {
    document.addEventListener('click', (e) => {
      const flyout = this.recentSearchesFlyoutTarget
      const termInput = this.termTarget
      if (!flyout.contains(e.target) && e.target !== termInput) {
        this.hideRecentSearches()
      }
    })
  }

  // Filter chip click — cycle both → applied → unapplied, then search.
  cycle(event) {
    const chip = event.currentTarget
    const field = this.fieldFor(chip)
    if (!field) return

    const current = field.value || STATES[0]
    const next = STATES[(STATES.indexOf(current) + 1 + STATES.length) % STATES.length]
    field.value = next
    this.syncChip(chip)
    this.search()
  }

  // Search term typing — keep the reset flag in sync and debounce the search.
  termChanged() {
    this.syncResetFlag()
    this.updateRecentSearchesFlyout()
    // Show the loading state immediately so the user gets instant feedback,
    // rather than only once the 250ms debounce actually fires the request.
    this.resultsTarget.classList.add('is-loading')
    this.scheduleSearch()
  }

  // Show recent searches when input is focused
  showRecentSearches() {
    const term = this.termTarget.value.trim()
    if (term === '') {
      this.updateRecentSearchesFlyout()
    }
  }

  // Save the term as a recent search when the user leaves the field. The
  // quick search is auto (debounced), so blur is the natural "commit" signal —
  // no submit button / Enter press is required.
  saveRecentSearchOnBlur(event) {
    // If focus moved into the flyout, the suggestion click saves instead.
    const next = event.relatedTarget
    if (next && this.recentSearchesFlyoutTarget.contains(next)) return

    const term = this.termTarget.value.trim()
    if (term) {
      RecentSearchesManager.add(term)
    }
  }

  // Update the recent searches flyout list
  updateRecentSearchesFlyout() {
    const query = this.termTarget.value
    const results = RecentSearchesManager.search(query)

    if (results.length === 0) {
      this.hideRecentSearches()
      return
    }

    const list = this.recentSearchesListTarget
    list.innerHTML = results
      .map(
        (search) =>
          `<li class="recent-searches-item" tabindex="-1">
            <svg class="recent-searches-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span data-search-term="${this.escapeHtml(search)}">${this.escapeHtml(search)}</span>
          </li>`
      )
      .join('')

    this.recentSearchesFlyoutTarget.classList.remove('hidden')
  }

  // Hide recent searches flyout
  hideRecentSearches() {
    this.recentSearchesFlyoutTarget.classList.add('hidden')
  }

  // Escape a search term before injecting it into the flyout's innerHTML.
  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  // Handle Enter/ArrowDown/Escape in search input
  handleSearchKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()

      // If a suggestion has focus (after ArrowDown), select it.
      const active = this.recentSearchesListTarget.querySelector('li:focus')
      if (active) {
        this.selectItem(active)
      } else {
        // Implicit form submission is blocked (text input + select and no
        // submit button), so save the term and search here instead.
        if (this.termTarget.value.trim()) {
          RecentSearchesManager.add(this.termTarget.value)
        }
        this.hideRecentSearches()
        this.search()
      }
      return
    }

    const list = this.recentSearchesListTarget
    if (list.children.length === 0) return

    if (event.key === 'Escape') {
      this.hideRecentSearches()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      const firstItem = list.children[0]
      if (firstItem) firstItem.focus()
    }
  }

  // Select a recent search from the flyout
  selectRecentSearch(event) {
    const item = event.target.closest('li')
    if (!item) return

    this.selectItem(item)
  }

  // Fill the input and search for the given flyout item.
  selectItem(item) {
    const searchTerm = item.querySelector('[data-search-term]')?.dataset.searchTerm
    if (!searchTerm) return

    this.termTarget.value = searchTerm
    this.syncResetFlag()
    this.hideRecentSearches()
    this.resultsTarget.classList.add('is-loading')

    // Save and search
    RecentSearchesManager.add(searchTerm)
    this.search()
  }

  // A select / date / checkbox changed — search immediately.
  changed() {
    if (this.clearing) return
    this.search()
  }

  // Reset every filter to its default and search again (client-side clear).
  clearSearch(event) {
    if (event) event.preventDefault()
    this.clearing = true

    // term + reset flag
    if (this.hasTermTarget) this.termTarget.value = ''
    this.syncResetFlag()

    // toggle chips back to their defaults
    const defaults = {
      quick_search_bookmarked: 'both',
      quick_search_assigned: 'both',
      quick_search_active: 'applied',
      quick_search_owing: 'both',
      quick_search_future_jobs: 'both'
    }
    Object.entries(defaults).forEach(([name, value]) => {
      const field = this.formTarget.querySelector(`input[name="${name}"]`)
      if (field) field.value = value
    })
    this.chipTargets.forEach((chip) => this.syncChip(chip))

    Object.entries(HIGHLIGHT_DEFAULTS).forEach(([name, value]) => {
      const field = this.formTarget.querySelector(`input[name="${name}"]`)
      if (field) field.value = value
    })
    this.syncHighlightToggles()

    // Tom Select selects back to empty
    this.formTarget.querySelectorAll('[data-controller~="select"]').forEach((el) => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select')
      if (ctrl && ctrl.instance) ctrl.instance.clear()
    })

    // datepickers back to empty
    this.formTarget.querySelectorAll('[data-controller~="datepicker"]').forEach((el) => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'datepicker')
      if (ctrl && ctrl.instance) ctrl.instance.clear()
    })

    // acquisitions checkbox off
    const acquisition = this.formTarget.querySelector('input[name="quick_search_acquisition"][type="checkbox"]')
    if (acquisition) acquisition.checked = false

    this.clearing = false
    this.search()
  }

  // Native form submit (Enter in the field or the Search button).
  submit(event) {
    if (event) event.preventDefault()
    // Save the search term to recent searches
    if (this.hasTermTarget && this.termTarget.value.trim()) {
      RecentSearchesManager.add(this.termTarget.value)
    }
    this.hideRecentSearches()
    this.search()
  }

  // Pagination links inside the results fragment.
  async paginate(event) {
    const link = event.target.closest('.ds-pagination a')
    if (!link) return

    event.preventDefault()
    const url = new URL(link.href, window.location.origin)
    url.searchParams.set('partial', '1')
    await this.load(url.toString())
    this.scrollToTable(this.resultsTarget)
  }

  // Pagination links inside the acquisitions fragment (fetched independently).
  async paginateAcquisitions(event) {
    const link = event.target.closest('.ds-pagination a')
    if (!link) return

    event.preventDefault()
    const url = new URL(link.href, window.location.origin)
    url.searchParams.set('acquisitions_partial', '1')
    url.searchParams.set('quick_search_acquisition', '1')
    await this.loadAcquisitionsFromUrl(url.toString())
    this.scrollToTable(this.acquisitionResultsTarget)
  }

  // After a page switch, jump to the start of the loaded table so the user
  // sees the new page's first rows rather than staying scrolled at the bottom.
  scrollToTable(container) {
    const table = container.querySelector('.ds-table')
    const target = table || container
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  scheduleSearch() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.search(), 250)
  }

  search() {
    clearTimeout(this.timer)
    this.load(this.buildUrl())
    this.loadAcquisitions()
  }

  async load(url) {
    this.resultsTarget.classList.add('is-loading')
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      this.resultsTarget.innerHTML = html
      this.updateTitle()
      this.updateClearVisibility()
      this.updateMoreFiltersBadge()
      this.syncHighlightToggles()
      this.syncUrl()
    } catch (error) {
      // Fall back to a full navigation so the user still gets a result/error.
      window.location.assign(url)
    } finally {
      this.resultsTarget.classList.remove('is-loading')
    }
  }

  buildUrl() {
    const base = this.formTarget.action.split('?')[0]
    const params = new URLSearchParams(new FormData(this.formTarget))
    params.set('partial', '1')
    return `${base}?${params.toString()}`
  }

  buildAcquisitionsUrl() {
    const base = this.formTarget.action.split('?')[0]
    const params = new URLSearchParams(new FormData(this.formTarget))
    params.set('acquisitions_partial', '1')
    params.delete('acquisitions_page')
    return `${base}?${params.toString()}`
  }

  async loadAcquisitions() {
    if (!this.hasAcquisitionResultsTarget) return

    const data = new FormData(this.formTarget)
    if (!data.getAll('quick_search_acquisition').includes('1')) {
      this.acquisitionResultsTarget.innerHTML = ''
      return
    }

    await this.loadAcquisitionsFromUrl(this.buildAcquisitionsUrl())
  }

  async loadAcquisitionsFromUrl(url) {
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      this.acquisitionResultsTarget.innerHTML = html
    } catch (error) {
      // Fall back to a full navigation so the user still gets a result/error.
      window.location.assign(url)
    }
  }

  syncUrl() {
    const base = this.formTarget.action.split('?')[0]
    const params = new URLSearchParams(new FormData(this.formTarget))
    window.history.replaceState({}, '', `${base}?${params.toString()}`)
  }

  updateTitle() {
    if (!this.hasTitleTarget) return

    const countEl = this.resultsTarget.querySelector('[data-results-count]')
    if (countEl && countEl.dataset.resultsCount) {
      this.titleTarget.textContent = `Customers (${countEl.dataset.resultsCount})`
    }
  }

  // Show "Clear search" only when the form differs from the default filters.
  updateClearVisibility() {
    if (!this.hasClearTarget) return
    this.clearTarget.classList.toggle('hidden', this.isDefaultState())
  }

  isDefaultState() {
    const data = new FormData(this.formTarget)
    const val = (name) => (data.get(name) || '').trim()

    if (val('quick_search_term')) return false
    if (val('quick_search_sort')) return false
    if (val('quick_search_area_id')) return false
    if (val('last_clean_from')) return false
    if (val('last_clean_to')) return false
    if (val('last_contacted_before')) return false
    if (data.getAll('quick_search_acquisition').includes('1')) return false

    const defaults = {
      quick_search_bookmarked: 'both',
      quick_search_assigned: 'both',
      quick_search_active: 'applied',
      quick_search_owing: 'both',
      quick_search_future_jobs: 'both'
    }
    return Object.entries(defaults).every(([name, def]) => (data.get(name) || def) === def)
  }

  // Show a marker on the "More filters" button when any of its extra filters
  // (sort, area, dates, acquisitions) are active.
  updateMoreFiltersBadge() {
    if (!this.hasMoreFiltersBadgeTarget) return
    this.moreFiltersBadgeTarget.classList.toggle('hidden', !this.hasMoreFilters())
  }

  hasMoreFilters() {
    const data = new FormData(this.formTarget)
    const val = (name) => (data.get(name) || '').trim()

    return !!(val('quick_search_sort') || val('quick_search_area_id') ||
      val('last_clean_from') || val('last_clean_to') || val('last_contacted_before') ||
      data.getAll('quick_search_acquisition').includes('1'))
  }

  syncResetFlag() {
    if (!this.hasResetTarget) return

    const empty = !this.hasTermTarget || this.termTarget.value === ''
    this.resetTarget.value = empty ? 'true' : 'false'
  }

  fieldFor(chip) {
    return this.formTarget.querySelector(`input[name="${chip.dataset.field}"]`)
  }

  syncChip(chip) {
    const field = this.fieldFor(chip)
    const value = field ? field.value || STATES[0] : STATES[0]
    const index = Math.max(STATES.indexOf(value), 0)

    STATES.forEach((state) => chip.classList.remove(state))
    chip.classList.add(STATES[index])

    const label = chip.dataset[`tooltip-${index}`]
    if (label) {
      chip.setAttribute('title', label)
      chip.setAttribute('aria-label', label)
    }
    chip.setAttribute('aria-pressed', value === 'both' ? 'false' : 'true')
  }

  toggleHighlight(event) {
    const button = event.currentTarget
    const field = this.formTarget.querySelector(`input[name="${button.dataset.field}"]`)
    if (!field) return

    field.value = field.value === '1' ? '0' : '1'
    this.syncHighlightToggle(button)
    this.search()
  }

  syncHighlightToggles() {
    this.highlightToggleTargets.forEach((button) => this.syncHighlightToggle(button))
  }

  syncHighlightToggle(button) {
    const field = this.formTarget.querySelector(`input[name="${button.dataset.field}"]`)
    const enabled = field ? field.value === '1' : true
    button.classList.toggle('is-disabled', !enabled)
    button.setAttribute('aria-pressed', String(enabled))
  }
}
